import type { Ref, ReactNode } from 'react';
import clsx from 'clsx';
import { SidebarCollapseProvider } from './sidebar-collapse';

export type AppShellProps = {
  sidebar: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
  /** Overrides the scrollable content region padding for route-specific Figma frames. */
  contentClassName?: string;
  /**
   * Ref to the scrollable content region (the `[data-shell-content]` div).
   * F-024: the `/uat` route attaches a ref here to capture and restore the
   * reviewer's scroll position across the Test Case detail round trip. Optional;
   * other AppShell callers are unaffected.
   */
  contentRef?: Ref<HTMLDivElement>;
};

/**
 * The application chrome shell: fixed sidebar on the left, sticky topbar
 * across the main column, scrollable content beneath. Every Figma page
 * lives inside this layout â pages render via the `children` slot.
 */
export function AppShell({
  sidebar,
  topbar,
  children,
  contentClassName,
  contentRef,
}: AppShellProps) {
  return (
    <SidebarCollapseProvider>
      <div className="grid h-screen min-h-[600px] grid-cols-[auto_1fr] overflow-hidden bg-surface-app font-sans">
        {sidebar}
        <section className="flex min-h-0 min-w-0 flex-col">
          {topbar}
          <div
            ref={contentRef}
            data-shell-content
            className={clsx('min-h-0 flex-1 overflow-auto', contentClassName ?? 'px-8 py-7')}
          >
            {children}
          </div>
        </section>
      </div>
    </SidebarCollapseProvider>
  );
}
